import { Typography } from '@mui/material'
import React, { Fragment, ChangeEvent, memo, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { Formik, FormikProps, useFormikContext } from 'formik'
import { BankInformationSchema, DebitCardSchema } from './BankPaymentForm.validator'
import {
  BankInformationRequest,
  BankPaymentFormProps,
  BankPaymentFormValues,
  DebitCardRequest,
} from './BankPaymentForm.interface'
import { BankPaymentFormType, PaymentRecipientNameChoice } from './BankPaymentForm.constants'
import { PaymentRecipientNameChoiceGroup } from './PaymentRecipientNameChoiceGroup'

import { Button } from 'src/components/shared/Button'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import { FormikRadioGroup } from 'src/components/shared/form/formik/FormikRadioGroup'
import { DebitCardContent } from './DebitCardContent'
import { BankInformationContent } from './BankInformationContent'
import { GSIcon } from 'src/components/shared/GSIcon'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { getPaymentMethodDetails, PaymentData } from 'src/utils/paymentMethod'
import { FetchPaymentMethod } from 'src/constants/actionTypes'
import {
  DriverPaymentMethod,
  PaymentMethodRecipientNamePreference,
  PaymentMethodTypeEnum,
} from 'src/interfaces/paymentMethod'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { getDriver } from 'src/selectors/driver'
import { InfoContainer } from '../shared/InfoContainer'
import { StepLabels } from '../client/step-labels/StepLabels'

const paymentMethodOptions = [{ label: 'Debit Card', value: BankPaymentFormType.DEBIT_CARD }]

const initialValues: BankPaymentFormValues = {
  nameOnCard: '',
  cardNumber: '',
  expirationDate: '',
  zipCode: '',
  routingNumber: '',
  accountNumber: '',
  accountType: undefined,
  formType: undefined,
  paymentRecipientNameChoice: PaymentRecipientNameChoice.Personal,
}

type RecipientNameFormSyncProps = {
  businessEntityStepCompleted: boolean
  personalName: string
  businessName: string
}

const RecipientNameFormSync = memo(
  ({ businessEntityStepCompleted, personalName, businessName }: RecipientNameFormSyncProps) => {
    const { values, setFieldValue } = useFormikContext<BankPaymentFormValues>()

    useEffect(() => {
      if (!businessEntityStepCompleted || !values.formType) {
        return
      }

      const resolved =
        values.paymentRecipientNameChoice === PaymentRecipientNameChoice.Business
          ? businessName
          : personalName

      if (values.nameOnCard === resolved) {
        return
      }

      setFieldValue('nameOnCard', resolved)
    }, [
      businessEntityStepCompleted,
      values.formType,
      values.paymentRecipientNameChoice,
      values.nameOnCard,
      personalName,
      businessName,
      setFieldValue,
    ])

    return null
  },
)

const getValidationSchema = (type?: BankPaymentFormType) => {
  const schemaMap = {
    [BankPaymentFormType.DEBIT_CARD]: DebitCardSchema,
    [BankPaymentFormType.BANK_INFORMATION]: BankInformationSchema,
  }

  if (!type) {
    return undefined
  }

  return schemaMap[type]
}

const BankPaymentFormComponent = ({
  title,
  description,
  showIcon,
  showStepIndex,
  showCurrentPaymentMethod,
  showFormLabel = true,
  onSubmitDebitCard,
  onSubmitBankInformation,
  paymentMethodData: paymentMethodDataProp,
  flow,
}: BankPaymentFormProps) => {
  const dispatch = useDispatch()
  const currentStep = useSelector(getCurrentCollectDataStep)
  const driver = useSelector(getDriver)
  const formikRef = useRef<FormikProps<BankPaymentFormValues>>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [currentFormType, setCurrentFormType] = useState<BankPaymentFormType | undefined>(undefined)
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<PaymentData | null>(null)
  const [paymentMethodData, setPaymentMethodData] = useState<DriverPaymentMethod | null>(
    paymentMethodDataProp || null,
  )

  const businessEntityStepCompleted = paymentMethodData?.hasBusinessEntity || false

  const personalLegalName = useMemo(
    () => (driver ? `${driver.firstName} ${driver.lastName}`.trim() : ''),
    [driver],
  )

  const businessEntityDisplayName = useMemo(
    () => paymentMethodData?.businessEntityName?.trim() ?? '',
    [paymentMethodData?.businessEntityName],
  )

  const getPaymentMethodLabel = (method: PaymentData): string => {
    return method?.type === PaymentMethodTypeEnum.Card ? 'Debit Card' : 'Bank Information'
  }

  useEffect(() => {
    const processPaymentMethod = (method: DriverPaymentMethod) => {
      if (showCurrentPaymentMethod) {
        const isCard = method.paymentMethodType === PaymentMethodTypeEnum.Card
        const mapped = isCard
          ? {
              type: method.paymentMethodType,
              lastDigits: method.cardNumberLast4 || '',
              expiration: method.cardExpiration || '',
            }
          : {
              type: method.paymentMethodType,
              accountLastDigits: method.bankAccountNumberLast4 || '',
              routingLastDigits: method.bankRoutingNumberLast4 || '',
            }
        setCurrentPaymentMethod(mapped)
      }
    }

    if (paymentMethodDataProp) {
      setPaymentMethodData(paymentMethodDataProp)
      processPaymentMethod(paymentMethodDataProp)

      return
    }

    const fetchPaymentMethod = async () => {
      try {
        const method: DriverPaymentMethod = await createAsyncAction(
          dispatch,
          FetchPaymentMethod.request({ flow }),
        )

        setPaymentMethodData(method)
        processPaymentMethod(method)
      } catch (e) {
        setPaymentMethodData(null)
        setCurrentPaymentMethod(null)
      }
    }

    fetchPaymentMethod()
  }, [dispatch, flow, showCurrentPaymentMethod, paymentMethodDataProp])

  const handleFormSubmit = async (values: BankPaymentFormValues) => {
    setIsLoading(true)
    setError(null)

    let formattedDate = ''

    if (values.expirationDate) {
      const cleanDate = values.expirationDate
        .replace(/undefined/g, '')
        .replace(/--/g, '-')
        .trim()
      formattedDate = cleanDate
    }

    const recipientNamePreference: PaymentMethodRecipientNamePreference =
      businessEntityStepCompleted &&
      values.paymentRecipientNameChoice === PaymentRecipientNameChoice.Business
        ? PaymentMethodRecipientNamePreference.BusinessName
        : PaymentMethodRecipientNamePreference.PersonalName

    try {
      const currentType = (values.formType as BankPaymentFormType) || BankPaymentFormType.DEBIT_CARD
      let result = null

      if (currentType === BankPaymentFormType.DEBIT_CARD) {
        const cleanCardNumber = (values.cardNumber || '').replace(/\s+/g, '')
        const requestBody: DebitCardRequest = {
          cardNumber: cleanCardNumber,
          cardExpiration: formattedDate,
          zipcode: values.zipCode || '',
          recipientNamePreference,
          ...(currentStep?.id && { stepId: currentStep.id }),
        }

        result = await onSubmitDebitCard(requestBody)
      } else if (currentType === BankPaymentFormType.BANK_INFORMATION) {
        const requestBody: BankInformationRequest = {
          routingNumber: values.routingNumber || '',
          accountNumber: values.accountNumber || '',
          accountType: values.accountType || '',
          recipientNamePreference,
          ...(currentStep?.id && { stepId: currentStep.id }),
          hasBusinessEntity: businessEntityStepCompleted,
        }

        result = await onSubmitBankInformation(requestBody)
      }

      if (!result) {
        const needsFormTypeRestore = values.formType && !currentFormType

        if (needsFormTypeRestore) {
          setCurrentFormType(values.formType)
        }

        return
      }

      formikRef.current?.resetForm()
      setCurrentFormType(undefined)
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const resolveNameForRecipientChoice = (): string => {
    if (!businessEntityStepCompleted) {
      return personalLegalName
    }

    const choice =
      formikRef.current?.values.paymentRecipientNameChoice ?? PaymentRecipientNameChoice.Personal

    if (choice === PaymentRecipientNameChoice.Business) {
      return businessEntityDisplayName
    }

    return personalLegalName
  }

  const handleTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newType = e.target.value as BankPaymentFormType

    setCurrentFormType(newType)

    formikRef.current?.setFieldValue('formType', newType)

    const prefilledName = resolveNameForRecipientChoice()

    if (prefilledName) {
      formikRef.current?.setFieldValue('nameOnCard', prefilledName)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={getValidationSchema(currentFormType)}
      innerRef={formikRef}
    >
      {({ handleSubmit, handleChange }) => {
        return (
          <div css={tw`h-full w-full flex flex-col`}>
            <div>
              {showIcon && (
                <div
                  css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-white mt-2 mb-4`}
                >
                  <GSIcon iconName="gs_wallet_3" height={20} width={20} />
                </div>
              )}
              <StepLabels hideStepIndex={!showStepIndex} />
              <Typography css={tw`my-2`} variant="h2">
                {title}
              </Typography>
              {description && (
                <Typography css={tw`mb-2 text-shades-400`}>
                  {businessEntityStepCompleted
                    ? "To receive payouts and process transactions, you'll need to add a valid settlement method."
                    : description}
                </Typography>
              )}

              {showCurrentPaymentMethod && currentPaymentMethod && (
                <Fragment>
                  <div css={tw`mt-4`}>
                    <Typography css={tw`text-shades-400 uppercase`} variant="overline">
                      Added settlement method
                    </Typography>
                  </div>
                  <div css={tw`flex mt-2 items-center flex-row p-4 rounded-2xl bg-shades-0`}>
                    <div>
                      <Typography css={tw`text-shades-1000`} variant="h4">
                        {getPaymentMethodLabel(currentPaymentMethod)}
                      </Typography>
                      <Typography css={tw`mt-0.5 text-shades-400 capitalize`}>
                        {getPaymentMethodDetails(currentPaymentMethod)}
                      </Typography>
                    </div>
                  </div>
                </Fragment>
              )}

              <div css={tw`mt-4 mb-6`}>
                <InfoContainer className={tw`mb-2 rounded-2xl p-0 py-3 px-4`}>
                  {businessEntityStepCompleted
                    ? 'You can add a debit card or bank account using either your personal name or your business name. Contact support if you need help.'
                    : 'No business information provided — please add your personal payment details to continue. Contact support if you need help.'}
                </InfoContainer>

                {showFormLabel && (
                  <Typography
                    css={tw`text-shades-400 text-xs font-bold uppercase`}
                    variant="overline"
                  >
                    Add settlement method
                  </Typography>
                )}

                <div css={tw`bg-shades-0 p-4 rounded-xl mt-1.5 relative overflow-hidden`}>
                  <div css={[tw`w-full`, currentFormType ? tw`mb-4` : tw`mb-0`]}>
                    <FormikRadioGroup
                      label="Select preferable option"
                      name="formType"
                      options={[
                        ...paymentMethodOptions,
                        {
                          label: 'Bank Information',
                          value: BankPaymentFormType.BANK_INFORMATION,
                        },
                      ]}
                      onChange={handleTypeChange}
                      dataTestId="payments-bank-form-type-input"
                    />
                  </div>
                  {currentFormType ? (
                    <Fragment>
                      {businessEntityStepCompleted && (
                        <Fragment>
                          <InfoContainer className={tw`mt-6 mb-4 rounded-2xl`} color="orange">
                            Ensure the name on your settlement method matches your selected option.
                          </InfoContainer>
                          <div css={tw`mb-4`}>
                            <PaymentRecipientNameChoiceGroup
                              personalName={personalLegalName}
                              businessName={businessEntityDisplayName}
                              sectionLabel={
                                currentFormType === BankPaymentFormType.DEBIT_CARD
                                  ? 'Name on card'
                                  : 'Name on account'
                              }
                              dataTestId="payments-recipient-name-choice"
                            />
                          </div>
                          <RecipientNameFormSync
                            businessEntityStepCompleted={businessEntityStepCompleted}
                            personalName={personalLegalName}
                            businessName={businessEntityDisplayName}
                          />
                        </Fragment>
                      )}
                      {currentFormType === BankPaymentFormType.DEBIT_CARD && (
                        <DebitCardContent
                          handleChange={handleChange}
                          driver={driver}
                          businessEntityStepCompleted={businessEntityStepCompleted}
                        />
                      )}

                      {currentFormType === BankPaymentFormType.BANK_INFORMATION && (
                        <BankInformationContent
                          businessEntityStepCompleted={businessEntityStepCompleted}
                          handleChange={handleChange}
                        />
                      )}
                    </Fragment>
                  ) : null}
                </div>
                {currentFormType ? (
                  <Button
                    className={tw`w-full mt-8`}
                    loading={isLoading}
                    onClick={() => handleSubmit()}
                    data-testid="payments-bank-submit-btn"
                  >
                    Submit and Continue
                  </Button>
                ) : null}
              </div>
            </div>
            <FormErrorMessage css={tw`mb-4`} error={error} />
          </div>
        )
      }}
    </Formik>
  )
}

export const BankPaymentForm = memo(BankPaymentFormComponent)
